import { useRef, useState, useCallback } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Ticket } from "lucide-react";
import { BOOK_URL, TICKETS_URL, M, scrollToId } from "../lib/site";
import { MaskLines, FadeUp } from "./Reveal";
import { FlyerLightbox } from "./FlyerLightbox";

const FACTS = [
  ["Date", "Friday 13 November"],
  ["Doors", "9PM til late"],
  ["Sound", "RNB · House · Afro House"],
  ["Expect", "Bottomless · Live DJs · Late nights"],
];

export const Flyer = () => {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "center center"] });
  const scale = useTransform(scrollYProgress, [0, 1], [0.86, 1]);
  const radius = useTransform(scrollYProgress, [0, 1], [48, 20]);

  return (
    <section id="opening" ref={ref} className="relative overflow-hidden py-24 sm:py-32" data-testid="opening-section">
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-[520px] w-[80vw] -translate-x-1/2 rounded-full bg-[var(--gold)] opacity-[0.12] blur-[160px]" />
      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-10">
        <div className="mb-12 flex flex-col items-start justify-between gap-8 sm:mb-16">
          <div>
            <p className="eyebrow mb-6">Grand Opening</p>
            <MaskLines lines={["Friday", "13 November"]}
              className="font-display whitespace-nowrap text-[3.4rem] leading-[0.9] text-[#F8F6F0] sm:text-7xl lg:text-[7.5rem]" />
          </div>
          <FadeUp delay={0.2} className="flex w-full shrink-0 flex-col gap-3 whitespace-nowrap sm:w-auto sm:flex-row">
            <a href={TICKETS_URL} target="_blank" rel="noopener noreferrer" data-testid="opening-tickets-btn"
              className="btn-gold inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-[12px] font-medium uppercase tracking-[0.2em]">
              <Ticket size={15} /> Get Tickets
            </a>
            <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" data-testid="opening-book-btn"
              className="btn-ghost inline-flex items-center justify-center gap-2 rounded-full border-[var(--glow)] px-8 py-4 text-[12px] font-medium uppercase tracking-[0.2em] text-[var(--glow)]">
              Book Bottomless <ArrowUpRight size={15} />
            </a>
            <button onClick={() => scrollToId("guestlist")} data-testid="opening-guestlist-btn"
              className="btn-ghost inline-flex items-center justify-center rounded-full px-8 py-4 text-[12px] font-medium uppercase tracking-[0.2em] text-[#F8F6F0]">
              Join the Guest List
            </button>
          </FadeUp>
          <p className="-mt-4 text-xs uppercase tracking-[0.2em] text-[var(--stone)]" data-testid="opening-bottomless-hours">Bottomless packages available 10PM – 12AM</p>
        </div>

        <motion.button type="button" onClick={() => setOpen(true)} style={{ scale, borderRadius: radius }}
          aria-label="Open flyer full screen"
          className="group relative block w-full overflow-hidden border border-[var(--line)] shadow-[0_40px_120px_-30px_rgba(230,207,167,0.35)]"
          data-testid="opening-flyer">
          <img src={M.flyer} alt="Eleganté grand opening flyer — Friday 13 November, 9PM til late, Level 1/412 Little Collins St"
            className="aspect-[1920/1005] w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.02]" />
          <span className="absolute bottom-3 right-3 rounded-full border border-[var(--line)] bg-[#09080B]/75 px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-[#F8F6F0] backdrop-blur-md transition-colors duration-300 group-hover:text-[var(--glow)] sm:bottom-5 sm:right-5">
            Tap to view · Save the date
          </span>
        </motion.button>
        <FlyerLightbox open={open} onClose={close} />

        <div className="mt-10 grid grid-cols-1 border-t border-[var(--line)] sm:grid-cols-2 lg:grid-cols-4">
          {FACTS.map(([k, v], i) => (
            <FadeUp key={k} delay={i * 0.08} className="border-b border-[var(--line)] py-6 lg:border-b-0 lg:border-r lg:px-6 lg:first:pl-0 lg:last:border-r-0">
              <p className="field-label mb-2">{k}</p>
              <p className="font-display text-xl text-[#F8F6F0] sm:text-2xl" data-testid={`opening-fact-${k.toLowerCase()}`}>{v}</p>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};
